import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import type { PermissionSlug } from '../../utils/permissions';
import ReadOnlyBanner from '../banners/ReadOnlyBanner';

interface ProtectedRouteProps {
  requiredPermission: PermissionSlug;
  /**
   * Corporate capability gate (a profile flag), orthogonal to the user's RBAC.
   * Omit it for routes gated by permission alone.
   */
  isEnabled?: boolean;
  children: ReactNode;
}

/**
 * Route-level guard. Wraps a route in RouterPrivate to prevent access on two
 * independent axes:
 *
 * - `requiredPermission`: what this *user* is allowed to do (RBAC).
 * - `isEnabled`: what this *corporate* has enabled (a profile flag).
 *
 * Users with 'all:all' (Super Admin) always pass through the permission check.
 * Users without the required permission are redirected to /home, and so are
 * users whose corporate does not have the capability enabled.
 * Users with read but without write see a persistent ReadOnlyBanner.
 *
 * @example
 * <ProtectedRoute requiredPermission="all:all">
 *   <SettingsView />
 * </ProtectedRoute>
 *
 * @example
 * <ProtectedRoute
 *   requiredPermission="development:read"
 *   isEnabled={datalogin?.is_child_corporate !== true}
 * >
 *   <Development />
 * </ProtectedRoute>
 */
const ProtectedRoute = ({
  requiredPermission,
  isEnabled,
  children,
}: ProtectedRouteProps) => {
  const { can, isProfileValidated } = usePermission();

  // Antes de que `getProfile` confirme los permisos, `datalogin` puede traer
  // el snapshot de `localStorage` de un login anterior con permisos que ya no
  // aplican. No decide con eso: espera un instante en vez de dejar pasar o
  // rebotar según un permiso desactualizado. Al arrancar desde
  // `localStorage`, esto también impide decidir `isEnabled` con ese snapshot.
  if (!isProfileValidated) {
    return null;
  }

  // `isEnabled === false` and not `!isEnabled`: the prop is optional, so an
  // omitted flag (`undefined`) must not block the route — only an explicit
  // `false` does.
  if (!can(requiredPermission) || isEnabled === false) {
    return <Navigate to="/home" replace />;
  }

  // Derive the write slug from the read slug (e.g. development:read → development:write)
  // and show the read-only banner when the user has read but not write access.
  const writeSlug = requiredPermission.replace(
    ':read',
    ':write',
  ) as PermissionSlug;
  const isReadOnly = requiredPermission.endsWith(':read') && !can(writeSlug);

  return (
    <>
      {/* Add bottom padding so fixed banner doesn't overlap page content */}
      <div className={isReadOnly ? 'pb-14' : ''}>{children}</div>
      {isReadOnly && <ReadOnlyBanner />}
    </>
  );
};

export default ProtectedRoute;
